// contexts/CountryContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { setCookie, getCookie } from '../utils/cookieUtils'
import { syncLocaleWithCountry } from '../i18n/I18nProvider'

const CountryContext = createContext()

export const VALID_COUNTRIES = ['ba', 'rs']

export const COUNTRY_CONFIG = {
  ba: {
    name: 'Bosna i Hercegovina',
    currency: 'KM',
    flag: '🇧🇦',
    locale: 'bs-BA',
    vat: '17%',
  },
  rs: {
    name: 'Srbija',
    currency: 'RSD',
    flag: '🇷🇸',
    locale: 'sr-RS',
    vat: '20%   ',
  },
  // hr: {
  //   name: 'Hrvatska',
  //   currency: 'EUR',
  //   flag: '🇭🇷',
  //   locale: 'hr-HR',
  //   vat: '25%',
  // },
}

export function getCountryIdByIdentifier(identifier, countries = []) {
  return countries.find((country) => country.identifier === identifier)?.id ?? null
}

export function getCountryById(countryId, countries = []) {
  return countries.find((country) => country.id === Number(countryId)) ?? null
}

export function getOrderCountryLabel(countryId, countryName = '', countries = []) {
  const match = countries.find((country) => country.id === Number(countryId))
  if (match) return match.name
  return countryName || '—'
}

export function getOrderCurrency(countryId, countries = []) {
  const match = countries.find((country) => country.id === Number(countryId))
  if (match?.identifier && COUNTRY_CONFIG[match.identifier]) {
    return COUNTRY_CONFIG[match.identifier].currency
  }
  return COUNTRY_CONFIG.ba.currency
}

// Global country state for when we need it outside of route context
let globalCountryState = {
  selectedCountry: null,
  listeners: new Set(),
  modalListeners: new Set(),
}

export function setGlobalCountry(country, skipCookie = false) {
  globalCountryState.selectedCountry = country
  syncLocaleWithCountry(country)
  globalCountryState.listeners.forEach((listener) => listener(country))

  if (!skipCookie) {
    setCookie('selectedCountry', country, 30) // Save for 30 days
  }
}

export function getGlobalCountry() {
  if (!globalCountryState.selectedCountry) {
    globalCountryState.selectedCountry = getCookie('selectedCountry') || null
  }
  return globalCountryState.selectedCountry
}

export function subscribeToCountryChange(listener) {
  globalCountryState.listeners.add(listener)
  return () => globalCountryState.listeners.delete(listener)
}

export function subscribeToModalState(listener) {
  globalCountryState.modalListeners.add(listener)
  return () => globalCountryState.modalListeners.delete(listener)
}

export function triggerCountryModal() {
  globalCountryState.modalListeners.forEach((listener) => listener(true))
}

export function CountryProvider({ children }) {
  const { countryCode } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Get saved country from cookie
  const savedCountry = getCookie('selectedCountry')

  // Determine current country priority: URL param > saved cookie > default 'ba'
  const validSavedCountry =
    savedCountry && VALID_COUNTRIES.includes(savedCountry) ? savedCountry : null
  const currentCountry =
    countryCode && VALID_COUNTRIES.includes(countryCode)
      ? countryCode
      : validSavedCountry || 'ba'

  // Initialize country selection logic
  useEffect(() => {
    const initializeCountry = () => {
      const currentPath = location.pathname

      // Check if we're on admin/special routes that don't need country selection
      const isSpecialRoute =
        currentPath.startsWith('/admin') ||
        currentPath.startsWith('/login') ||
        currentPath.startsWith('/korisnik') ||
        currentPath.startsWith('/verification') ||
        currentPath.startsWith('/password-change') ||
        currentPath.startsWith('/odrzavanje')

      // If we're on a special route, don't show modal
      if (isSpecialRoute) {
        setIsInitialized(true)
        return
      }

      // If no saved country and not on country route, show modal
      if (!savedCountry && !countryCode) {
        console.log('No saved country found, showing modal')
        setShowModal(true)
      } else {
        // Set the country in global state without creating cookie yet
        if (currentCountry) {
          setGlobalCountry(currentCountry, true) // Skip cookie creation for now
        }
      }

      setIsInitialized(true)
    }

    const timer = setTimeout(initializeCountry, 100)
    return () => clearTimeout(timer)
  }, [savedCountry, countryCode, currentCountry, location.pathname])

  // Handle cookie creation/update only when country is explicitly selected or changed
  const selectCountry = useCallback(
    (newCountryCode, isUserSelection = false) => {
      if (!VALID_COUNTRIES.includes(newCountryCode)) {
        console.error('Invalid country code:', newCountryCode)
        return
      }

      const previousCountry = savedCountry

      // Always update global state
      setGlobalCountry(newCountryCode)

      // Create or update cookie
      if (isUserSelection || !previousCountry) {
        console.log('Creating/updating country cookie:', newCountryCode)
        setCookie('selectedCountry', newCountryCode, 30)
      } else if (previousCountry && previousCountry !== newCountryCode) {
        console.log(
          'Country changed from',
          previousCountry,
          'to',
          newCountryCode,
        )
        setCookie('selectedCountry', newCountryCode, 30)
      }

      // Close modal if open
      if (showModal) {
        setShowModal(false)
      }

      // Navigate to country-specific route if needed
      const currentPath = location.pathname
      let newPath

      if (currentPath === '/' || currentPath === '') {
        newPath = `/${newCountryCode}`
      } else if (!currentPath.startsWith(`/${newCountryCode}`)) {
        // Remove any existing country code and add the new one
        let cleanPath = currentPath
        VALID_COUNTRIES.forEach((country) => {
          if (cleanPath.startsWith(`/${country}`)) {
            cleanPath = cleanPath.replace(`/${country}`, '')
          }
        })
        newPath = `/${newCountryCode}${cleanPath || ''}`

        setIsLoading(true)
        navigate(newPath, { replace: true })
        setTimeout(() => setIsLoading(false), 300)
      }

      window.location.href = newPath
    },
    [savedCountry, showModal, location.pathname, navigate],
  )

  const changeCountry = useCallback(
    (newCountryCode) => {
      selectCountry(newCountryCode, true) // Mark as user selection
    },
    [selectCountry],
  )

  const getLocalizedPath = useCallback(
    (path) => {
      // Ensure path starts with country code
      if (path.startsWith('/')) {
        if (path.startsWith(`/${currentCountry}`)) {
          return path
        }
        return `/${currentCountry}${path}`
      }
      return `/${currentCountry}/${path}`
    },
    [currentCountry],
  )

  // Subscribe to global modal triggers
  useEffect(() => {
    return subscribeToModalState(setShowModal)
  }, [])

  const value = {
    currentCountry,
    countryConfig: COUNTRY_CONFIG[currentCountry],
    allCountries: VALID_COUNTRIES,
    allCountryConfigs: COUNTRY_CONFIG,
    changeCountry,
    selectCountry,
    getLocalizedPath,
    isLoading,
    showModal,
    setShowModal,
    isInitialized,
    hasSavedCountry: !!savedCountry,
  }

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  )
}

export function useCountry() {
  const context = useContext(CountryContext)
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider')
  }
  return context
}

// Hook for getting country outside of provider context
export function useGlobalCountry() {
  const [country, setCountry] = useState(() => getGlobalCountry())

  useEffect(() => {
    return subscribeToCountryChange(setCountry)
  }, [])

  const selectGlobalCountry = useCallback((countryCode) => {
    setGlobalCountry(countryCode)
    // Also trigger modal close globally
    globalCountryState.modalListeners.forEach((listener) => listener(false))
  }, [])

  return {
    country,
    selectCountry: selectGlobalCountry,
    hasCountry: !!country,
  }
}

// Hook for navigation with country code
export function useCountryNavigation() {
  const navigate = useNavigate()
  const { getLocalizedPath, currentCountry } = useCountry()

  const navigateWithCountry = useCallback(
    (path, options = {}) => {
      const localizedPath = getLocalizedPath(path)
      console.log(
        `Navigating with country ${currentCountry}: ${path} -> ${localizedPath}`,
      )
      navigate(localizedPath, options)
    },
    [navigate, getLocalizedPath, currentCountry],
  )

  // Also provide the regular navigate function in case it's needed
  const navigateWithoutCountry = useCallback(
    (path, options = {}) => {
      navigate(path, options)
    },
    [navigate],
  )

  return {
    navigate: navigateWithCountry, // Default country-aware navigate
    navigateWithCountry, // Explicit country-aware navigate
    navigateWithoutCountry, // Regular navigate without country
    getLocalizedPath, // Utility function to get localized path
  }
}

// Utility function to check if country selection is needed
export function shouldShowCountryModal() {
  const savedCountry = getCookie('selectedCountry')
  const currentPath = window.location.pathname

  const isSpecialRoute =
    currentPath.startsWith('/admin') ||
    currentPath.startsWith('/login') ||
    currentPath.startsWith('/korisnik') ||
    currentPath.startsWith('/verification') ||
    currentPath.startsWith('/password-change') ||
    currentPath.startsWith('/odrzavanje')

  const hasCountryInPath = VALID_COUNTRIES.some((country) =>
    currentPath.startsWith(`/${country}`),
  )

  return !savedCountry && !hasCountryInPath && !isSpecialRoute
}
